import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/** A label taller than this many line boxes is treated as wrapped. */
const MULTILINE_LINE_BOX_THRESHOLD = 1.6;

/** Used when `line-height` computes to `normal`, which parses as NaN. */
const NORMAL_LINE_HEIGHT_RATIO = 1.25;

/**
 * How long to wait for the float↔rest font-size transition before measuring.
 * Matches `animation-transition('all 0.3s ease')` on `label.placeholder` in
 * `_globals.scss`, plus a small buffer for late `transitionend` delivery.
 */
const LABEL_SETTLE_FALLBACK_MS = 350;

/**
 * Reports whether a floating placeholder wraps onto more than one line, so the
 * field around it can grow instead of letting the translated text spill out of
 * the box.
 *
 * Both checkout fields that carry a floating placeholder (voucher code and
 * special note) render a translated string inside a fixed-height input, and both
 * overflow in the languages whose translation runs to two or three words. They
 * share this directive rather than each keeping their own copy of the
 * measurement.
 *
 * Usage — two-way bound, so the host component can drive the container class:
 *
 * ```html
 * <div #inputGroup class="input-group" [class.has-multiline-placeholder]="isMultiline">
 *   <input class="form-control" />
 *   <label
 *     [(appMultilineLabel)]="isMultiline"
 *     [multilineLabelGroup]="inputGroup"
 *     [multilineLabelAtRest]="!isFocused && !value"
 *   >{{ 'some.key' | translate }}</label>
 * </div>
 * ```
 */
@Directive({
  selector: '[appMultilineLabel]',
})
export class MultilineLabelDirective
  implements AfterViewInit, OnChanges, OnDestroy
{
  /** Current state; two-way bound via `appMultilineLabelChange`. */
  @Input() appMultilineLabel = false;

  /**
   * The element whose width decides whether the label wraps (normally the input
   * group). Observed alongside the label so a viewport resize re-measures.
   *
   * Takes the raw element, which is what a template reference variable on a
   * plain element hands over (`#inputGroup` → `HTMLElement`, not `ElementRef`).
   * Binding a `@ViewChild` here instead looks equivalent and is not: the query
   * result lands after the first binding pass, so the directive sees
   * `undefined` in `ngAfterViewInit` and never observes the group — and the
   * late-arriving value raises NG0100 in dev mode.
   */
  @Input() multilineLabelGroup?: HTMLElement;

  /**
   * False while the label is floated, i.e. the field is focused or filled. A
   * floated label is forced onto a single line, so measuring it in that state
   * would always report "one line" and the field would visibly shrink the moment
   * the shopper taps into it and grow back on blur. While this is false the last
   * resting measurement is kept.
   */
  @Input() multilineLabelAtRest = true;

  @Output() appMultilineLabelChange = new EventEmitter<boolean>();

  @HostBinding('class.is-multiline') get multilineClass(): boolean {
    return this.appMultilineLabel;
  }

  private resizeObserver: ResizeObserver | null = null;
  private destroy$: Subject<void> = new Subject<void>();

  /**
   * True while the label is animating back to its resting size after blur.
   * Measuring mid-transition (font-size still ~12px) falsely reports single-line
   * and collapses the field; the ResizeObserver would then flip it back as the
   * font grows. Both paths are gated here until `transitionend` (or the
   * fallback timer) fires.
   */
  private settling = false;
  private settleFallbackTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly onLabelTransitionEnd = (event: TransitionEvent) => {
    // Only the font-size step matters for wrap detection; ignore top/left noise.
    if (event.target !== this.el.nativeElement) {
      return;
    }
    if (event.propertyName && event.propertyName !== 'font-size') {
      return;
    }
    this.finishSettling();
  };

  constructor(
    private el: ElementRef<HTMLElement>,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
  ) {}

  ngAfterViewInit() {
    // Do not measure synchronously here: ngAfterViewInit runs inside the host
    // view's change-detection pass, after host/class bindings have already been
    // checked. A wrapping label would flip the flag and raise NG0100 in dev
    // mode. ResizeObserver is specified to deliver one initial notification
    // asynchronously (already routed through zone.run); without it, measure
    // once on the next microtask.
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() =>
        this.zone.run(() => this.measure()),
      );
      this.resizeObserver.observe(this.el.nativeElement);
      if (this.multilineLabelGroup) {
        this.resizeObserver.observe(this.multilineLabelGroup);
      }
    } else {
      Promise.resolve().then(() => this.measure());
    }

    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.measure());
  }

  ngOnChanges(changes: SimpleChanges) {
    // Blurring an empty field puts the label back at rest. The float→rest
    // font-size transition is still running, so measuring immediately (or via
    // the ResizeObserver mid-tween) would report a one-line floated size and
    // collapse the field. Wait for the animation to settle first.
    if (
      changes.multilineLabelAtRest &&
      !changes.multilineLabelAtRest.firstChange
    ) {
      if (this.multilineLabelAtRest) {
        this.beginSettling();
      } else {
        this.cancelSettling();
      }
    }
  }

  /** Re-measure the label and emit when the wrapped/unwrapped state changes. */
  measure() {
    const el = this.el.nativeElement;
    if (!el || !this.multilineLabelAtRest || this.settling) {
      return;
    }

    const style = getComputedStyle(el);
    const fontSize = parseFloat(style.fontSize) || 16;
    let lineHeight = parseFloat(style.lineHeight);
    if (Number.isNaN(lineHeight)) {
      lineHeight = fontSize * NORMAL_LINE_HEIGHT_RATIO;
    }

    const multiline =
      el.getBoundingClientRect().height >
      lineHeight * MULTILINE_LINE_BOX_THRESHOLD;
    if (multiline === this.appMultilineLabel) {
      return;
    }

    this.appMultilineLabel = multiline;
    this.appMultilineLabelChange.emit(multiline);
    this.cdr.markForCheck();
  }

  ngOnDestroy() {
    this.cancelSettling();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.destroy$.next();
    this.destroy$.complete();
  }

  private beginSettling() {
    this.cancelSettling();
    this.settling = true;
    this.el.nativeElement.addEventListener(
      'transitionend',
      this.onLabelTransitionEnd,
    );
    // Fallback if transitionend never fires (reduced-motion, no transition, etc.).
    this.settleFallbackTimer = setTimeout(
      () => this.finishSettling(),
      LABEL_SETTLE_FALLBACK_MS,
    );
  }

  private finishSettling() {
    if (!this.settling) {
      return;
    }
    this.cancelSettling();
    this.measure();
  }

  private cancelSettling() {
    this.settling = false;
    this.el.nativeElement.removeEventListener(
      'transitionend',
      this.onLabelTransitionEnd,
    );
    if (this.settleFallbackTimer !== null) {
      clearTimeout(this.settleFallbackTimer);
      this.settleFallbackTimer = null;
    }
  }
}
